<template>
    <div class="h-auto bg-white border shadow-sm rounded-lg overflow-hidden" style="min-height: 50px;">
        <transition name="fade-fast" mode="out-in">
            <b-skeleton-img v-if="loading" key="stepsLoading" no-aspect height="50px" width="100%"></b-skeleton-img>
            <ol
                v-else
                key="stepsNotLoading"
                class="step-selectors w-100 h-100 d-flex flex-wrap list-unstyled mb-0"
            >  
                <li 
                    v-for="(step, index) in steps"
                    :key="step[valueField]"
                    class="step-selectors-inner position-relative d-flex justify-content-center align-items-center"
                    :class="[
                        isSelected(step) ? 'selected' : '',
                        !isAvailable(step) ? 'unavailable' : '',
                        `step-${step[valueField]}`,
                    ]"
                    style="flex-basis: 0%; flex-grow: 1; flex-shrink: 1;"
                >  <!-- :key="step[valueField]" or :key="hashKey" -->
                    
                    <button 
                        class="step-selectors-button h-100 w-100 d-flex align-items-center justify-content-center"
                        :disabled="!isAvailable(step)"
                        @click="handleClick(step, index)"
                    >
                        <div class="">
                            <transition name="fade-medium" mode="out-in">
                            <!-- to make transition work, make sure there is nothing above this that also has transition, or else that will do its thing, this gets missed -->
                            <b-icon 
                                v-if="step.isError" 
                                :key="`${step[valueField]}-error`"
                                icon="x-circle-fill" 
                                variant="danger" 
                                class="rounded-circle step-icon step-icon-show-color step-icon-error bg-danger-100" 
                                style="padding: 6px;" 
                                font-scale="2"
                            ></b-icon>
                            <b-icon 
                                v-else-if="step.isSuccess" 
                                :key="`${step[valueField]}-success`"
                                icon="check-circle-fill" 
                                variant="success" 
                                class="rounded-circle step-icon step-icon-show-color step-icon-success bg-success-100" 
                                style="padding: 6px;" 
                                font-scale="2"
                            ></b-icon>
                            <b-icon 
                                v-else-if="step.isCurrent" 
                                :key="`${step[valueField]}-current`"
                                icon="circle-fill"
                                variant="primary" 
                                class="rounded-circle step-icon step-icon-show-color step-icon-primary bg-primary-100" 
                                style="padding: 6px;" 
                                font-scale="2"
                            ></b-icon>  <!-- animation="throb"  -->
                            <b-icon 
                                v-else-if="step.icon" 
                                :key="`${step[valueField]}-icon`"
                                :icon="step.icon" 
                                :variant="step.color_variant || 'primary'" 
                                class="rounded-circle step-icon" 
                                :class="`bg-${step.color_variant || 'primary'}-100`" 
                                style="padding: 6px;" 
                                font-scale="2"
                            ></b-icon>
                            <!-- If not anything special, will show here - keep these grayed when not hovered, but keep colors of other ones if they are complete -->
                            <div 
                                v-else 
                                :key="`${step[valueField]}-noicon`"
                                class="p-2 step-icon rounded-circle font-weight-bold d-flex justify-content-center align-items-center" 
                                :class="`bg-${step.color_variant || 'primary'}-100 text-${step.color_variant || 'primary'}-600`" 
                                style="height: 2rem; width: 2rem;"
                            >
                                {{ index + 1 }}
                            </div>
                            </transition>
                        </div>
                        <div class="ml-3 text-left">
                            <div class="font-weight-bold text-title">
                                {{ step[titleField] }}
                                <b-badge 
                                    v-if="step.showBadge" 
                                    variant="soft-danger text-uppercase ml-3"
                                    v-b-tooltip.hover
                                    title="This step has open tasks assigned to you"
                                >You</b-badge>
                            </div>
                            <div class="small text-muted text-description">{{ step[descriptionField] }}</div>
                        </div>
                    </button>
                    <div 
                        v-if="(index + 1) < steps.length"
                        class="d-none d-md-block position-absolute h-100" style="top: 0; right: 0; width: 20px;" aria-hidden="true"
                    >
                        <svg class="h-100 w-100 text-gray-300" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                            <path d="M0 -2L20 40L0 82" vector-effect="non-scaling-stroke" stroke="currentcolor" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <!-- <div
                        class="selected-bar" 
                        :style="{
                            width: ((index + 1) < steps.length) ? 'calc(100% - 2px)' : 'calc(100% + 18px)',
                            backgroundColor: isSelected(step) ? `var(--${step.color_variant || 'primary'})` : '',
                        }"
                    ></div> -->
                    <div
                        class="selected-bar" 
                        :style="{
                            width: ((index + 1) < steps.length) ? 'calc(100% - 2px)' : 'calc(100% + 18px)',
                            backgroundColor: isSelected(step) ? `var(--secondary)` : '',
                        }"
                    ></div>
                </li>
            </ol>
        </transition>
    </div>
</template>

<script>

export default {
    name: 'StepSelectors',
    components: {},
    mixins: [],
    props: {
        steps: {  // Array of Objects with these keys:  
        // { value, disabled, title, description, icon, color_variant, isError, isSuccess, isCurrent }
            type: Array,
            required: true,
        },
        selected: {  // the 'value' of the seleected step - this whole thing is basically an input
            // NOTE - the CURRENT step (the one the actual process is currently in, will have isCurrent=true on it -- but this is independent of 'selected' since user may be looking at a step that is not the current one)
            type: String,
            required: false,
        },
        loading: { // force it into loading state
            type: Boolean,
            default: false,
        },
        valueField: {
            type: String,
            default: 'value',
        },
        titleField: {
            type: String,
            default: 'title',
        },
        descriptionField: {
            type: String,
            default: 'description',
        },
        disabledField: {
            type: String,
            default: 'disabled',
        },
    },
    data: function() {
        return {}
    },
    computed: {
    },
    mounted: function() {
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        isSelected(step) {
            return step[this.valueField] === this.selected;
        },
        isAvailable(step) {
            return !step[this.disabledField];
        },
        handleClick(step, index) {
            this.$emit('selected', step, index);
        }
    }
}
</script>

<style lang="scss" scoped>

.step-selectors {
    background-color: #fff;

    .step-selectors-inner {
        // this is the li for each step, button and bar and arrow are direct children
        // this is what gets .selected
        transition: 0.2s all;
        opacity: 0.7;

        button.step-selectors-button {
            background-color: #fff;  // to override normal button styles
            border: none;  // override button styles
            padding: 0.75rem 1rem;
            transition: 0.2s all;
            //border-bottom: 4px solid transparent;
            .text-title {
                transition: 0.2s all;
                color: $gray-600;
            }
            .step-icon {transition: 0.2s all;}
        }
        .selected-bar {
            background-color: transparent;
            transition: 0.2s all;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            position: absolute;
            bottom: 0; 
            left: -18px; 
            height: 4px;
        }
        &:hover {
            //border-bottom-color: var(--gray-200);
            opacity: 1;
            button.step-selectors-button {
                .text-title {
                    color: $gray-900;
                }
            }
            .selected-bar {
                //display: block;
                background-color: $gray-200;
            }
        }
        &.selected {
            //border-bottom-color: var(--primary);
            opacity: 1;
            button.step-selectors-button {
                .text-title {
                    color: $gray-900;
                }
            }
            .selected-bar {
                //background-color: var(--primary);
                background-color: var(--secondary);
            }
        }
        &:not(.selected):not(:hover) {
            button.step-selectors-button {
                .step-icon:not(.step-icon-show-color) {
                    background-color: $gray-100;  // remove colored background and text on non-active, non-hover
                    color: $gray-400 !important;  // need !important to override important on text-success/text-danger etc
                }
                /* .text-title {
                    color: $gray-500;
                } */
            }
        }
        &.unavailable:not(.selected) {
            button.step-selectors-button {
                cursor: not-allowed;
                .text-title {
                    color: $gray-400;
                }
                .step-icon {
                    background-color: $gray-100;  // remove colored background and text on non-active, non-hover
                    color: $gray-400 !important;  // need !important to override important on text-success/text-danger etc
                }
            }
            .selected-bar {
                background-color: transparent;
            }
        }
        


    }

    
}

</style>